import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showRules, setShowRules] = useState(false);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordRules, setPasswordRules] = useState({
    minLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
  });

  const navigate = useNavigate();

  const checkPasswordRules = (password) => {
    setPasswordRules({
      minLength: password.length >= 8,
      hasUpper: /[A-Z]/.test(password),
      hasLower: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const renderRule = (isValid, text) => (
    <li className={isValid ? 'valid' : 'invalid'}>
      <i className={`fas ${isValid ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
      {text}
    </li>
  );

  const validateForm = () => {
    if (!newPassword || !confirmPassword) {
      setError('All fields are required');
      return false;
    }

    const isPasswordValid = Object.values(passwordRules).every(Boolean);
    if (!isPasswordValid) {
      setError('Password does not meet required criteria');
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const payload = {
        action: 'resetPasswordByToken',
        email,
        token,
        newPassword,
      };

      const response = await axios.post(API_URL, payload);

      toast.success(response.data?.message || 'Password reset successfully');

      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Failed to reset password';

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          .password-rules {
            margin-top: 8px;
            padding-left: 0;
            list-style: none;
            font-size: 13px;
          }

          .password-rules li {
            margin-bottom: 5px;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .password-rules li.valid {
            color: green;
          }

          .password-rules li.invalid {
            color: #dc2626;
          }

          .password-field {
            position: relative;
          }

          .password-toggle-btn {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            border: none;
            background: transparent;
            cursor: pointer;
            color: #6b7280;
            font-size: 16px;
          }

          .password-toggle-btn:hover {
            color: #111827;
          }
            .login-input-line {
  padding-right: 40px; /* space for eye icon */
}
        `}
      </style>

      <div className="login-page">
        <div className="login-left-panel">
          <div className="login-left-brand">
            <span className="login-brand-dot"></span>
            <div>
              <img
                src="/assets/img/Website_logo_color logo-black text-1.png"
                alt="logo"
                className="login-brand-logo"
              />
            </div>
          </div>

          <div className="login-left-illustration">
            <img
              src="/assets/img/forgot.png"
              alt="Reset password"
              className="login-left-image"
            />
          </div>
        </div>

        <div className="login-right-panel">
          <div className="login-right-inner">
            <h5 className="login-heading">Reset Password</h5>

            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
              Enter your new password below.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="login-field">
                <label className="login-label">New Password</label>

                <div className="login-input-row password-field">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className={`login-input-line ${error ? 'error' : ''}`}
                    placeholder="Enter new password"
                    value={newPassword}
                    onFocus={() => setShowRules(true)}
                    onBlur={() => setShowRules(false)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewPassword(value);
                      checkPasswordRules(value);

                      if (confirmPassword && value !== confirmPassword) {
                        setError("Passwords do not match");
                      } else {
                        setError("");
                      }
                    }} 
                  />

                  <button
                    type="button"
                    className="password-toggle-btn "
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    <i className={`fas ${showNewPassword ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                  </button>
                </div>

                {showRules && (
                  <ul className="password-rules">
                    {renderRule(passwordRules.minLength, 'At least 8 characters')}
                    {renderRule(passwordRules.hasUpper, 'At least one uppercase letter')}
                    {renderRule(passwordRules.hasLower, 'At least one lowercase letter')}
                    {renderRule(passwordRules.hasNumber, 'At least one number')}
                    {renderRule(passwordRules.hasSpecial, 'At least one special character')}
                  </ul>
                )}
              </div>
              <div className="login-field">
                <label className="login-label">Confirm Password</label>

                <div className="login-input-row password-field">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={`login-input-line ${error ? 'error' : ''}`}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => {
                      const value = e.target.value;
                      setConfirmPassword(value);

                      if (newPassword && value && newPassword !== value) {
                        setError("Passwords do not match");
                      } else {
                        setError("");
                      }
                    }}
                  />

                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <i className={`fas ${showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                  </button>
                </div>
              </div>

              {error && (
                <span className="error-message" role="alert">
                  <i className="fas fa-exclamation-circle"></i> {error}
                </span>
              )}

              <button
                type="submit"
                className={`login-primary-btn ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
                style={{ marginTop: '16px', marginBottom: '16px' }}
              >
                {isLoading ? (
                  <>
                    <LoadingIndicator size={22} />
                    <span style={{ marginLeft: '8px' }}>Resetting...</span>
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>

              <div className="login-forgot" style={{ textAlign: 'center' }}>
                <Link to="/">
                  <i className="fas fa-arrow-left"></i> Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;